import {
	Versabuilt,
	TopJawInterface,
	BaseJaw,
	TopJaw,
	JawAssembly,
	ModularComponentFamily,
	VSCPartData,
	VSCPartJson,
	VSCPartDataHistoryRecord,
	RobotProgramAudit,
	RobotProgramCheck,
} from '../../interfaces/VersaBuilt.Interface';
import { api } from '../slices/api';

export const extendedApiSlice = api.injectEndpoints({
	endpoints: (builder) => ({
		getMillAutomationSystems: builder.query<Versabuilt[], void>({
			query: () => 'versabuilt/mill-automation-systems/',
		}),
		createVSCProgram: builder.mutation({
			query: ({ id, body }) => ({
				url: `versabuilt/mill-automation-systems/${id}/create-vsc-program/`,
				method: 'POST',
				body: body,
			}),
		}),
		updateVSCPartData: builder.mutation<
			VSCPartData,
			{ id: string; orderLineItemId: string; json_data: VSCPartJson }
		>({
			query: ({ id, json_data }) => ({
				url: `versabuilt/vsc-part-data/${id}/`,
				method: 'PATCH',
				body: { json_data },
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: 'OrderLineItem', id: arg.orderLineItemId },
				'RobotProgramAudit',
			],
		}),
		createVSCPartData: builder.mutation<
			VSCPartData,
			{ orderLineItemId: string; json_data: VSCPartJson }
		>({
			query: ({ orderLineItemId, json_data }) => ({
				url: 'versabuilt/vsc-part-data/new/',
				method: 'POST',
				body: { order_line_item: orderLineItemId, json_data },
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: 'OrderLineItem', id: arg.orderLineItemId },
				'RobotProgramAudit',
			],
		}),
		duplicateVSCPartData: builder.mutation<
			VSCPartData,
			{ id: string; orderLineItemId: string; name: string }
		>({
			query: ({ id, name }) => ({
				url: `versabuilt/vsc-part-data/${id}/duplicate/`,
				method: 'POST',
				body: { name },
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: 'OrderLineItem', id: arg.orderLineItemId },
				'RobotProgramAudit',
			],
		}),
		checkVSCPartData: builder.query<RobotProgramCheck, VSCPartJson>({
			query: (json_data) => ({
				url: 'versabuilt/vsc-part-data/check/',
				method: 'POST',
				body: { json_data },
			}),
		}),
		getRobotProgramAudit: builder.query<RobotProgramAudit, void>({
			query: () => 'versabuilt/vsc-part-data/audit/',
			providesTags: ['RobotProgramAudit'],
		}),
		getVSCPartData: builder.query<
			VSCPartData & { order_line_item: string },
			string
		>({
			query: (id) => `versabuilt/vsc-part-data/${id}/`,
		}),
		getVSCPartDataHistory: builder.query<VSCPartDataHistoryRecord[], string>({
			query: (id) => `versabuilt/vsc-part-data/${id}/history/`,
		}),
		getJawAssemblies: builder.query<JawAssembly[], void>({
			query: () => 'versabuilt/jaw-assemblies/',
		}),
		getJawAssemblyConfigurations: builder.query<JawAssembly[], void>({
			query: () => 'versabuilt/jaw-assemblies-configurations/',
		}),
		getTopJawInterfaces: builder.query<TopJawInterface[], void>({
			query: () => 'versabuilt/top-jaw-interfaces/',
		}),
		getBaseJaws: builder.query<BaseJaw[], void>({
			query: () => 'versabuilt/base-jaws/',
		}),
		getTopJaws: builder.query<TopJaw[], void>({
			query: () => 'versabuilt/top-jaws/',
		}),
		getModularComponentFamilies: builder.query<ModularComponentFamily[], void>({
			query: () => 'versabuilt/modular-components-families',
		}),
	}),
});

// Export hooks for usage in functional components
export const {
	useGetMillAutomationSystemsQuery,
	useCreateVSCProgramMutation,
	useUpdateVSCPartDataMutation,
	useCreateVSCPartDataMutation,
	useDuplicateVSCPartDataMutation,
	useGetVSCPartDataHistoryQuery,
	useGetRobotProgramAuditQuery,
	useCheckVSCPartDataQuery,
	useLazyGetVSCPartDataQuery,
	useGetJawAssembliesQuery,
	useGetJawAssemblyConfigurationsQuery,
	useGetTopJawInterfacesQuery,
	useGetBaseJawsQuery,
	useGetTopJawsQuery,
	useGetModularComponentFamiliesQuery,
} = extendedApiSlice;
